import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { sessionInfoAtom } from '../state';
import { Appointment, Provider } from '../types';

export type GetTelehealthSessionInfoVariables = {
  providerShortId: string | undefined;
  patientShortId: string | undefined;
};

export type TelehealthSessionInfoData_TelehealthSessionInfo_Provider = Pick<
  Provider,
  'image' | 'name' | 'shortId' | 'pronouns' | 'isPrescriber'
>;

export type TelehealthSessionInfoData_TelehealthSessionInfo_Appointment = Pick<
  Appointment,
  'timeEnd' | 'timeStart' | 'shortId' | 'clientUserShortId' | 'hasProviderNoShowReport'
>;

export type TelehealthSessionInfoData = {
  telehealthSessionInfo: {
    provider: TelehealthSessionInfoData_TelehealthSessionInfo_Provider;
    appointment: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
  };
};

export const GetTelehealthSessionInfoGql = gql`
  query GetTelehealthSessionInfo($providerShortId: String!, $patientShortId: String) {
    telehealthSessionInfo(providerShortId: $providerShortId, patientShortId: $patientShortId) {
      provider {
        name
        shortId
        image
        pronouns
        isPrescriber
      }
      appointment {
        hasProviderNoShowReport
        timeStart
        clientUserShortId
        timeEnd
        shortId
      }
    }
  }
`;

function useHandleGetTelehealthSessionInfoSuccessCallback() {
  const setSessionInfo = useSetAtom(sessionInfoAtom);
  return useCallback(
    (data: TelehealthSessionInfoData) => {
      setSessionInfo(data?.telehealthSessionInfo);
    },
    [setSessionInfo],
  );
}

export function useGetTelehealthSessionInfo(providerShortId: string | undefined, patientShortId: string | undefined) {
  const handleSuccess = useHandleGetTelehealthSessionInfoSuccessCallback();
  return useQuery<TelehealthSessionInfoData, GetTelehealthSessionInfoVariables>(GetTelehealthSessionInfoGql, {
    variables: { providerShortId, patientShortId },
    skip: !providerShortId,
    context: { skipAuth: true },
    onCompleted: handleSuccess,
  });
}

export function useGetTelehealthSessionInfoCallback() {
  const handleSuccess = useHandleGetTelehealthSessionInfoSuccessCallback();
  return useLazyQuery<TelehealthSessionInfoData, GetTelehealthSessionInfoVariables>(GetTelehealthSessionInfoGql, {
    context: { skipAuth: true },
    onCompleted: handleSuccess,
  });
}
